import BottomSheetModal from '@/components/modules/modals/BottomSheetModal';
import Modal from '@/components/modules/modals/redesign/Modal/Modal';
import { getModalTitle } from '@/features/payments/utils/paymentConfig.utils';
import { useAppSelector } from '@/hooks';
import useMobileView from '@/hooks/useMobileView';
import { useWidgetPayment } from '@/hooks/useWidgetPayment';
import { _t } from '@/locale';
import { SelectedPaymentMethod } from '@/types/checkout';
import PopupWaitScreen from '../PopupWaitScreen/PopupWaitScreen';

type WidgetPaymentModalProps = {
  isOpen: boolean;
  paymentMethodType: SelectedPaymentMethod['type'];
};

/**
 * The checkout's payment modal while the frame's pay popup is live: the sheet and
 * modal pair the Adyen component uses, never dismissible, showing the wait screen
 * until the popup reports or closes and the reconciling message until a reconcile settles.
 * The terminal states keep that screen until the checkout's reaction closes the
 * modal or routes: never an empty body, never the Adyen component, whose session
 * is stale by then. One wait screen for every state, so its live region announces
 * the change rather than a new element.
 */
const WidgetPaymentModal = ({ isOpen, paymentMethodType }: WidgetPaymentModalProps) => {
  const { isMobileView } = useMobileView();
  const user = useAppSelector((state) => state.users?.user);
  const { state, focusPopup } = useWidgetPayment();
  const title = getModalTitle(paymentMethodType, !!user);
  const waiting = state === 'opening' || state === 'open';

  const content = (
    <div className="p-3xl">
      <PopupWaitScreen
        message={waiting ? _t('completePaymentInWindow') : _t('features.bookingWidget.WidgetPayment.reconciling')}
        {...(waiting && {
          showWindowLabel: _t('features.bookingWidget.WidgetPayment.showWindow'),
          onShowWindow: focusPopup,
          autoFocusShowWindow: true,
        })}
      />
    </div>
  );

  if (isMobileView) {
    return (
      <BottomSheetModal isOpen={isOpen} dismissible={false} title={title}>
        {content}
      </BottomSheetModal>
    );
  }

  // No onRequestClose: react-modal closes on Escape only through it.
  return (
    <Modal isOpen={isOpen} shouldCloseOnOverlayClick={false}>
      <Modal.Content newDesign={true} contentPadding={false} floating={true} contentMaxWidth={500}>
        <Modal.Header newDesign={true} floating={true} title={title} />
        {content}
      </Modal.Content>
    </Modal>
  );
};

export default WidgetPaymentModal;
